import { inject, Injectable } from '@angular/core';
import {
  AIMode,
  ConversationApiService,
  ConversationChannel,
  CreateConversationResponse,
  GetMultiConversationDetailsV2Request,
  GetMultiConversationDetailsOptions,
  MediaInterface,
  MessageType,
  MetadataIdentifier,
  Participant,
  PlatformLocation,
  SendMessageResponse,
} from '@vendasta/conversation';
import { listConversations$, sendMessage$ as protoSendMessage$ } from '@vendasta/protos/conversation/v1';
import type { ListConversationsResponseInboxConversation } from '@vendasta/protos/conversation/v1';
import type { ConversationChannel as ProtoConversationChannel } from '@vendasta/protos/conversation/v1';
import { catchError, defaultIfEmpty, EMPTY, firstValueFrom, map, Observable, of } from 'rxjs';
import { ConversationDetail } from './interface/conversation.interface';
import { fromFirestoreId } from './conversation-utils';
import { FieldMask } from '@vendasta/contacts';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { buildSendMessageTrackProperties } from './conversation-utils';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class ConversationStatelessService {
  private readonly conversationApiService = inject(ConversationApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly analyticsService = inject(ProductAnalyticsService);

  listConversations(req: {
    accountGroupId: string;
    participantId: string;
    pageSize: number;
    cursor: string;
    channels: ConversationChannel[];
    isOpen: boolean | undefined;
    unreadOnly: boolean;
    awaitingResponseOnly: boolean;
    isAnonymous?: boolean;
    assigneeUserId?: string;
    searchTerm?: string;
  }): Observable<{ conversations: ListConversationsResponseInboxConversation[]; nextCursor: string }> {
    return listConversations$({
      accountGroupId: req.accountGroupId,
      participantId: req.participantId,
      pagingOptions: { pageSize: req.pageSize, cursor: req.cursor || '' },
      filters: {
        channels: req.channels as unknown as ProtoConversationChannel[], // SDK vs proto enum: structurally identical, nominally different
        isOpen: req.isOpen,
        unreadOnly: req.unreadOnly,
        awaitingResponseOnly: req.awaitingResponseOnly,
        isAnonymous: req.isAnonymous,
        assigneeUserId: req.assigneeUserId ?? '',
        searchTerm: req.searchTerm ?? '',
      },
    }).pipe(
      map((response) => ({
        conversations: response.conversations ?? [],
        nextCursor: response.pagingMetadata?.nextCursor ?? '',
      })),
    );
  }

  /**
   * create a conversation using the Conversation SDK/API
   * @param {Participant[]} participants - A list of participants
   * @param {ConversationChannel} conversationChannel - A conversation channel
   * @param originLocation - The origin location of the conversation
   * @param instanceId - The instance ID of the conversation, this should typically be empty
   * @return {Observable<CreateConversationResponse>} - An observable that contains a CreateConversationResponse
   */
  createConversation(
    participants: Participant[],
    conversationChannel: ConversationChannel,
    originLocation: PlatformLocation,
    instanceId = '',
  ): Observable<CreateConversationResponse> {
    if (participants.length === 0) {
      console.error(
        'The participants are empty. It`s necessary send at least two participants to create a conversation',
      );
      return of({} as CreateConversationResponse);
    }

    if (conversationChannel === ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED) {
      console.error('The conversationChannel is undefined. It`s necessary send a valid ConversationChannel');
      return of({} as CreateConversationResponse);
    }

    return this.conversationApiService
      .createConversation({
        participants: participants,
        channel: conversationChannel,
        originLocation: originLocation,
        instanceId: instanceId,
      })
      .pipe(
        catchError((err) => {
          console.warn('error to createConversation', err);
          return EMPTY;
        }),
        defaultIfEmpty({} as CreateConversationResponse),
      );
  }

  /**
   * Get conversation details by conversation ID
   * @param {string} conversationId - The conversation ID
   * @param {boolean} disableParticipantBranding - Whether to disable participant branding in the response.
   * @return {Observable<ConversationDetail>} - An observable that contains the conversation details
   */
  getConversationDetail(conversationId: string, disableParticipantBranding?: boolean): Observable<ConversationDetail> {
    conversationId = fromFirestoreId(conversationId);

    return this.getMultiConversationDetails([conversationId], undefined, disableParticipantBranding).pipe(
      map((conversationsDetails) => {
        return conversationsDetails[0];
      }),
    );
  }

  /**
   * Get multiple conversation details by conversation IDs
   * @param {string[]} conversationIds - The conversation IDs
   * @param {string[]} exclusions - Elements of the response conversation details to exclude. Defaults to exclude conversation_summary only.
   * @param {boolean} disableParticipantBranding - Whether to disable participant branding in the response.
   * @return {Observable<ConversationDetail[]>} - An observable that contains the conversation details.
   * The conversation summary and message metadata will be excluded to prevent large responses.
   */
  getMultiConversationDetails(
    conversationIds: string[],
    exclusions: string[] = ['conversation_summary'],
    disableParticipantBranding?: boolean,
  ) {
    if (conversationIds.length === 0) {
      return of([]);
    }

    const req = new GetMultiConversationDetailsV2Request();
    req.conversationIds = conversationIds;

    req.options = new GetMultiConversationDetailsOptions({
      exclusions: new FieldMask({ paths: exclusions }),
      disableParticipantBranding: disableParticipantBranding || false,
    });

    return this.conversationApiService.getMultiConversationDetailsV2(req).pipe(
      map((response) => response.conversations),
      catchError((error) => {
        console.warn('error to getMultiConversationDetails collections', error);
        if (error?.status === 403) {
          this.snackbarService.openErrorSnack('INBOX.ERROR.PERMISSION_DENY');
        }
        return of([]);
      }),
    );
  }

  async sendMessage(
    conversationId: string,
    type: MessageType,
    body: string,
    channel: ConversationChannel,
    originLocation: PlatformLocation,
    media: (MediaInterface & { transcodingJobId?: string })[] = [],
    sender: Participant,
    recipient?: Participant,
    metadata?: {
      identifier: MetadataIdentifier;
      data: {
        [key: string]: string;
      };
    }[],
    skipMessageDelivery?: boolean,
    aiMode?: AIMode,
  ): Promise<SendMessageResponse> {
    if (!conversationId) {
      throw new Error('The conversationId is empty. It`s necessary send a valid conversationId');
    }

    if (!body && media.length === 0 && type !== MessageType.MESSAGE_TYPE_TEMPLATE) {
      throw new Error('The body is empty. It`s necessary send a valid body');
    }

    if (channel === ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED || channel === null) {
      throw new Error('The channel is invalid. It`s necessary send a valid channel');
    }

    if (!originLocation) {
      throw new Error('The originLocation is invalid. It`s necessary send a valid originLocation');
    }

    const trackingProps = buildSendMessageTrackProperties(
      conversationId,
      sender,
      channel,
      media.length,
      originLocation,
    );

    try {
      const options: { skipMessageDelivery?: boolean; aiMode?: AIMode } = {
        skipMessageDelivery,
      };
      if (aiMode) {
        options.aiMode = aiMode;
      }

      const result = await firstValueFrom(
        protoSendMessage$({
          sender: sender as any,
          recipient: recipient as any,
          conversationId,
          type: type as any,
          body,
          originLocation: originLocation as any,
          media: media.map((m) => ({
            mediaContentType: m.mediaContentType,
            mediaLocationPath: m.mediaLocationPath,
            mediaFileName: m.mediaFileName,
            fileSize: m.fileSize,
            mediaUrl: m.mediaUrl,
            transcodingJobId: m.transcodingJobId ?? '',
          })),
          channel: channel as any,
          metadata: (metadata ?? []) as any,
          options: options as any,
        }) as unknown as Observable<SendMessageResponse>,
      );
      if (!result) {
        console.warn('ConversationStatelessService sendMessage value', result);
        return result;
      }

      this.analyticsService.trackEvent('inbox', 'conversation', 'send-message-success', 0, trackingProps);

      return result;
    } catch (httpErrorResponse) {
      this.analyticsService.trackEvent('inbox', 'conversation', 'send-message-error', 0, trackingProps);
      console.error('ConversationStatelessService sendMessage error', httpErrorResponse);
      const message = (httpErrorResponse as HttpErrorResponse)?.error?.message;
      if (message) {
        this.snackbarService.openErrorSnack(message);
      }
      throw httpErrorResponse;
    }
  }
}
